import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Clock } from 'lucide-react';
import emailjs from '@emailjs/browser';

const Contact = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const form = formRef.current!;
      
      // Send via EmailJS
      const templateParams = {
        from_name: form.user_name.value,
        reply_to: form.user_email.value,
        message: `
          NOUVEAU MESSAGE DU SITE WEB
          -------------------------
          Nom: ${form.user_name.value}
          Email: ${form.user_email.value}
          Téléphone: ${form.user_phone.value}
          -------------------------
          Message:
          ${form.message.value}
          -------------------------
        `,
        to_name: "Expert Maintenance Solutions",
        subject: "Nouveau Contact - Expert Maintenance Solutions"
      };

      // Send to both services in parallel
      await Promise.all([
        // EmailJS (Gmail)
        emailjs.send(
          'service_p0nxo4b',
          'template_w5tu9qx',
          templateParams,
          'MezUvv9N8LiszgrEv'
        ),
        
        // FormSubmit (Hostinger Email)
        fetch('https://formsubmit.co/contact@emsmaroc.com', {
          method: 'POST',
          body: new FormData(form),
          headers: {
            'Accept': 'application/json'
          }
        })
      ]);

      setSubmitStatus('success');
      form.reset();
    } catch (error) {
      setSubmitStatus('error');
      console.error('Error sending email:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Add this array with real contact info
  const contactInfo = [
    {
      icon: Phone,
      title: "Téléphone",
      content: ["+212 6 61 30 41 85", "+212 6 90 28 60 05"],
      links: ["tel:+212661304185", "tel:+212690286005"]
    },
    {
      icon: Mail,
      title: "Email",
      content: ["contact@emsmaroc.com", "commercial@emsmaroc.com"],
      links: ["mailto:contact@emsmaroc.com", "mailto:commercial@emsmaroc.com"]
    },
    {
      icon: MapPin,
      title: "Adresse",
      content: ["30 Rue Omar Riffi, 20250 Casablanca, Maroc"],
    },
    {
      icon: Clock,
      title: "Horaires du bureau",
      content: ["Lundi - Samedi: 9h - 18h", "En cas d'urgence, contactez-nous par téléphone"],
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1a237e]/5 to-white">
      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-[#1a237e]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Contactez-Nous
            </h1>
            <p className="text-xl text-white/80 max-w-3xl mx-auto">
              Notre équipe est à votre disposition pour répondre à toutes vos questions
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Info & Form Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div className="space-y-8">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                <h2 className="text-3xl font-bold text-[#1a237e] mb-8">
                  Informations de Contact
                </h2>
                <div className="space-y-6">
                  {contactInfo.map((item, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.2 }}
                      className="flex items-start space-x-4"
                    >
                      <div className="w-12 h-12 bg-[#1a237e]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        {React.createElement(item.icon, { className: "w-6 h-6 text-[#1a237e]" })}
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-[#1a237e]">{item.title}</h3>
                        <div className="space-y-1">
                          {item.content.map((line, i) => (
                            item.links ? (
                              <a 
                                key={i}
                                href={item.links[i]} 
                                className="block text-gray-600 hover:text-[#1a237e] transition-colors"
                              >
                                {line}
                              </a>
                            ) : (
                              <p key={i} className="text-gray-600">{line}</p>
                            )
                          ))}
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white p-8 rounded-xl shadow-lg border border-[#1a237e]/10"
            >
              <h2 className="text-3xl font-bold text-[#1a237e] mb-8">
                Envoyez-nous un message
              </h2>
              
              {submitStatus === 'success' && (
                <div className="mb-6 p-4 bg-green-50 text-green-700 rounded-lg">
                  Message envoyé avec succès! Nous vous répondrons dans les plus brefs délais.
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="mb-6 p-4 bg-red-50 text-red-700 rounded-lg">
                  Une erreur s'est produite. Veuillez réessayer ou nous contacter directement.
                </div>
              )}

              <form ref={formRef} onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-gray-700 mb-2" htmlFor="user_name">
                    Nom complet
                  </label>
                  <input
                    type="text"
                    name="user_name"
                    id="user_name"
                    required
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-[#1a237e] focus:ring-2 focus:ring-[#1a237e]/20 outline-none transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 mb-2" htmlFor="user_email">
                    Email
                  </label>
                  <input
                    type="email"
                    name="user_email"
                    id="user_email"
                    required
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-[#1a237e] focus:ring-2 focus:ring-[#1a237e]/20 outline-none transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 mb-2" htmlFor="user_phone">
                    Téléphone
                  </label>
                  <input
                    type="tel"
                    name="user_phone"
                    id="user_phone"
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-[#1a237e] focus:ring-2 focus:ring-[#1a237e]/20 outline-none transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 mb-2" htmlFor="message">
                    Message
                  </label>
                  <textarea
                    name="message"
                    id="message"
                    rows={4}
                    required
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-[#1a237e] focus:ring-2 focus:ring-[#1a237e]/20 outline-none transition-colors"
                  ></textarea>
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`w-full bg-[#1a237e] text-white py-3 px-6 rounded-lg font-semibold
                    transition-all duration-200 ${
                      isSubmitting 
                        ? 'opacity-70 cursor-not-allowed'
                        : 'hover:bg-[#1a237e]/90'
                    }`}
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Envoi en cours...
                    </span>
                  ) : (
                    'Envoyer le message'
                  )}
                </button>
              </form>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Add Map Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="rounded-xl overflow-hidden shadow-lg border border-[#1a237e]/10"
          >
            <iframe
              title="Notre localisation"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3323.846399132721!2d-7.632492684878927!3d33.592416880730094!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzPCsDM1JzMyLjciTiA3wrAzNyc1Ny4wIlc!5e0!3m2!1sen!2sus!4v1625764283576!5m2!1sen!2sus"
              width="100%"
              height="450"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Contact;